'use client';

import base, { AxiosInstance } from 'axios';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { useRBACOptional } from '@/contexts/RBACContext';
import { fetchSubTenantMemberUserIds, resolveOrgIdForSubTenantMemberFetch } from '@/lib/subTenantMemberUserIds';
import {
  getActiveTenantHeaders,
  readActiveTenantScope,
  resolveActiveRequestUserId,
} from '@/lib/activeTenantScope';
import { getMainApiOrigin, getSelfOriginForRedirects } from '@/config/apiConfig';
import { extractApiMessage } from './Axios';
import { SessionContext } from './Session';

export const DirectMailAxiosContext = createContext<AxiosInstance | null>(null);

/** DM calls must go to the Direct Mail service host (dm.*), not the generic SPA/API host. */
function resolveDirectMailBaseUrl(): string {
  const fallback = 'https://dm.staging-api.boostt.ai';
  const raw = process.env.NEXT_PUBLIC_DM_API_URL?.trim();
  if (!raw) return fallback;
  try {
    const u = new URL(raw);
    // Common deploy mistake: NEXT_PUBLIC_DM_API_URL=https://staging-api.boostt.ai — routes hit main `/api/*` instead of DM.
    if (u.hostname === 'staging-api.boostt.ai') {
      return 'https://dm.staging-api.boostt.ai';
    }
    return raw.replace(/\/+$/, '');
  } catch {
    return fallback;
  }
}

/** SWR/cache key: sub-tenant scope, member IDs, or account (e.g. reset to owner). */
export const DirectMailSubTenantMemberKeyContext = createContext<string>('');

export const isCancel = base.isCancel;

function isCampaignsListUrl(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/^\/+/, '');
  return path === 'campaigns/list' || path.endsWith('/campaigns/list');
}

function isInformedDeliveryListUrl(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/^\/+/, '');
  return (
    path === 'informedDelivery/campaign/list' ||
    path.endsWith('/informedDelivery/campaign/list')
  );
}

function isAnalyticsSummaryUrl(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/^\/+/, '');
  return (
    path === 'campaigns/analytics/summary' ||
    path.endsWith('/campaigns/analytics/summary')
  );
}

/** Add or remove `member_user_ids` query for sub-tenant aggregate listing (DM POST list, ID GET list). */
function applyCampaignListMemberScope(
  url: string,
  scopeActive: boolean,
  memberIds: number[] | null
): string {
  const pathPart = url.split('?')[0];
  const qs = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  const sp = new URLSearchParams(qs);
  if (!scopeActive) {
    sp.delete('member_user_ids');
    const next = sp.toString();
    return next ? `${pathPart}?${next}` : pathPart;
  }
  const ids = memberIds ?? [];
  // An empty `member_user_ids=` matches no one (list not loaded yet, or no org id) — omit it
  // so the request falls back to the user's own scope instead of returning nothing.
  if (ids.length === 0) {
    sp.delete('member_user_ids');
    const rest = sp.toString();
    return rest ? `${pathPart}?${rest}` : pathPart;
  }
  sp.set('member_user_ids', ids.map((n) => String(n)).join(','));
  return `${pathPart}?${sp.toString()}`;
}

function setQueryUserId(url: string, sessionUserId: string | number): string {
  const pathPart = url.split('?')[0];
  const qs = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  const sp = new URLSearchParams(qs);
  const uid = String(sessionUserId);
  sp.set('userID', uid);
  sp.set('user_id', uid);
  return `${pathPart}?${sp.toString()}`;
}

/**
 * `getAuthData()` sets `token = userId` when no JWT exists (legacy compat).
 * That value must not be sent as `session_token` on multipart or JSON bodies.
 */
function isRealSessionToken(
  token: string | null | undefined,
  userId: string | number | null | undefined
): boolean {
  if (token == null || token === '') return false;
  if (userId != null && String(token) === String(userId)) return false;
  return true;
}

function getDirectMailInstance(
  auth: { userId?: string | number | null; token?: string | null; isAuthenticated?: boolean },
  setSessionExpiredOpen: (open: boolean) => void,
  aggregateMemberIdsRef: MutableRefObject<number[] | null>,
  subTenantScopeActiveRef: MutableRefObject<boolean>,
  listingSubAccountIdRef: MutableRefObject<number | null>
) {
  const axios = base.create({
    baseURL: resolveDirectMailBaseUrl(),
    withCredentials: true,
  });

  axios.interceptors.request.use((req) => {
    const method = (req.method || 'get').toLowerCase();
    const tenantScope = readActiveTenantScope();
    const scopedAccountId =
      listingSubAccountIdRef.current ??
      tenantScope.activeAccountId ??
      tenantScope.rootListingAccountId ??
      null;
    // Impersonation scopes exactly like a direct login by the impersonated user (RBACContext
    // resets/refetches scope for them), same as the main Dashboard — so no impersonation carve-out.
    const accountAggregateActive = subTenantScopeActiveRef.current || scopedAccountId != null;

    // Aggregate lists must key off the session user (owner), not the switched member.
    const switchedMemberId = tenantScope.activeUserId;
    const sessionUserId = auth?.userId ?? null;
    const useSessionUserForAggregate =
      accountAggregateActive &&
      sessionUserId != null &&
      switchedMemberId != null &&
      String(switchedMemberId) !== String(sessionUserId);
    const activeUserId = useSessionUserForAggregate
      ? sessionUserId
      : resolveActiveRequestUserId(auth?.userId);

    if (!req.data && method !== 'get') {
      req.data = {};
    }

    if (method !== 'get' && activeUserId) {
      if (req.data instanceof FormData) {
        const fd = req.data;
        const hasUserField =
          fd.has('userID') || fd.has('user_id') || fd.has('userId');
        if (!hasUserField) {
          fd.append('userID', String(activeUserId));
        }
        if (
          auth?.isAuthenticated &&
          isRealSessionToken(auth.token, auth.userId) &&
          !fd.has('session_token')
        ) {
          fd.append('session_token', auth.token as string);
        }
      } else if (typeof req.data === 'object' && req.data !== null) {
        const body = req.data as Record<string, unknown>;
        const hasUserField = Boolean(
          body.userID ?? body.user_id ?? body.userId
        );
        if (!hasUserField) {
          body.userID = activeUserId;
        }
        if (
          auth?.isAuthenticated &&
          isRealSessionToken(auth.token, auth.userId) &&
          body.session_token == null
        ) {
          body.session_token = auth.token;
        }
      }
    }

    if (method === 'post' && req.url && isCampaignsListUrl(req.url)) {
      if (sessionUserId != null && accountAggregateActive) {
        req.url = setQueryUserId(req.url, sessionUserId);
        if (req.data && typeof req.data === 'object' && !(req.data instanceof FormData)) {
          const body = req.data as Record<string, unknown>;
          body.userID = String(sessionUserId);
          body.user_id = String(sessionUserId);
        }
      }
      req.url = applyCampaignListMemberScope(
        req.url,
        accountAggregateActive,
        aggregateMemberIdsRef.current
      );
    }
    if (method === 'get' && req.url && isInformedDeliveryListUrl(req.url)) {
      if (sessionUserId != null && accountAggregateActive) {
        req.url = setQueryUserId(req.url, sessionUserId);
      }
      req.url = applyCampaignListMemberScope(
        req.url,
        accountAggregateActive,
        aggregateMemberIdsRef.current
      );
    }
    if (method === 'get' && req.url && isAnalyticsSummaryUrl(req.url)) {
      if (sessionUserId != null && accountAggregateActive) {
        req.url = setQueryUserId(req.url, sessionUserId);
      }
      req.url = applyCampaignListMemberScope(
        req.url,
        accountAggregateActive,
        aggregateMemberIdsRef.current
      );
    }

    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    Object.assign(req.headers, getActiveTenantHeaders());
    // For non-org-owner users, rbac_selected_subtenant_id is null in localStorage so
    // getActiveTenantHeaders() returns no X-Active-Account-Id. Set it from the effective
    // listing scope so resolve_scoped_member_user_ids doesn't 403 when member_user_ids is present.
    if (scopedAccountId != null && !req.headers['X-Active-Account-Id']) {
      req.headers['X-Active-Account-Id'] = String(scopedAccountId);
    }
    if (sessionUserId != null && accountAggregateActive) {
      // Prefer session user for X-User-ID on aggregate lists (matches cookie when in sync).
      req.headers['X-User-ID'] = String(sessionUserId);
    } else if (activeUserId) {
      req.headers['X-User-ID'] = String(activeUserId);
    }
    if (typeof document !== 'undefined' && document.cookie) {
      req.headers['cookies'] = document.cookie;
    }

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      const config = err.config;
      const isUnrecoverable = err.response && [401, 403, 404].includes(err.response.status);

      if (config && !isUnrecoverable && (!config.__retryCount || config.__retryCount < 1)) {
        config.__retryCount = config.__retryCount || 0;
        config.__retryCount += 1;
        return new Promise((resolve) => setTimeout(() => resolve(axios(config)), 1000));
      }

      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (
            auth?.isAuthenticated &&
            err.response.config.url !== `${getMainApiOrigin()}/api/logout`
          ) {
            location.href = `${getSelfOriginForRedirects()}/v2/login`;
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError = (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError = 'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function DirectMailAxios({ children }: { children: ReactNode }) {
  const authData = useContext(AuthContext);
  const sessionContext = useContext(SessionContext);
  const rbac = useRBACOptional();
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);
  const subTenantScopeActiveRef = useRef(false);
  const listingSubAccountIdRef = useRef<number | null>(null);

  const tenantScope = readActiveTenantScope();
  const listingSubAccountId =
    rbac?.effectiveListingSubAccountId ??
    (rbac?.isRootContextActive && rbac?.defaultAccountId != null ? rbac.defaultAccountId : null) ??
    rbac?.selectedSubAccountId ??
    tenantScope.activeAccountId ??
    tenantScope.rootListingAccountId ??
    null;
  const isSubTenantListingScope =
    Boolean(listingSubAccountId) || Boolean(tenantScope.isAggregateSubTenantScope);
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }
  subTenantScopeActiveRef.current = isSubTenantListingScope;
  listingSubAccountIdRef.current = listingSubAccountId;

  const ownerUserIdRef = useRef<number | null>(null);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id
    ? Number(rbac.userContext.owner_user_id)
    : null;
  const defaultAccountIdRef = useRef<number | null>(null);
  defaultAccountIdRef.current = rbac?.defaultAccountId ?? null;

  useEffect(() => {
    if (!listingSubAccountId) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    // currentOrgId can be null on first paint / right after impersonation resets RBAC.
    const orgId = resolveOrgIdForSubTenantMemberFetch(rbac?.currentOrgId, sessionContext?.user);
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Include org owner so root-member direct login still sees owner-created campaigns.
        const ownerExtId = ownerUserIdRef.current;
        const rootAccId = defaultAccountIdRef.current;
        if (
          ownerExtId != null &&
          Number.isFinite(ownerExtId) &&
          (subId === rootAccId || tenantScope.rootListingAccountId === subId) &&
          !ids.includes(ownerExtId)
        ) {
          ids.push(ownerExtId);
        }
        if (!cancelled) {
          aggregateMemberIdsRef.current = ids;
          const sorted = [...ids].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    listingSubAccountId,
    rbac?.currentOrgId,
    sessionContext?.user,
    tenantScope.rootListingAccountId,
  ]);

  const auth = authData || {};
  const client = useMemo(
    () => getDirectMailInstance(auth, setSessionExpiredOpen, aggregateMemberIdsRef, subTenantScopeActiveRef, listingSubAccountIdRef),
    [authData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const effectiveMemberKey = isSubTenantListingScope ? subTenantMemberKey : '';
  const directMailSubTenantScopeSignal = [
    effectiveMemberKey,
    String(listingSubAccountId ?? ''),
    String(rbac?.currentAccountId ?? ''),
    String(rbac?.defaultAccountId ?? ''),
  ].join('|');

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    if (typeof window !== 'undefined') {
      location.href = `${getSelfOriginForRedirects()}/v2/login`;
    }
  };

  return (
    <>
      <DirectMailAxiosContext.Provider value={client}>
        <DirectMailSubTenantMemberKeyContext.Provider value={directMailSubTenantScopeSignal}>
          {children}
        </DirectMailSubTenantMemberKeyContext.Provider>
      </DirectMailAxiosContext.Provider>

      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
